import React, { useCallback } from 'react'
import CarHirePurchaseSummaryLocation from 'components/Pages/MyEscapesPage/PurchaseSummary/Common/CarHirePurchaseSummary/CarHirePurchaseSummaryLocation'
import TextButton from 'LuxKit/Button/TextButton'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import useCarHireReservationInfo from 'hooks/CarHire/useCarHireReservationInfo'
import { canRefundCarHireItem } from 'lib/order/orderItemUtils'

interface Props {
  order: App.Order
  isPastOrder: boolean
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
}

export default function LesaCarHirePurchaseSummary(props: Props) {
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap } = props

  const dispatch = useAppDispatch()

  const item = order.carHireItems[0]
  const reservation = item?.reservation

  const sameLocation = reservation?.pickUp?.locationName === reservation?.dropOff?.locationName

  const carHireReservation = useCarHireReservationInfo(item.idReservation ?? '', order.purchasingCustomerId)
  const reservationInfo = carHireReservation.reservation

  const canCarHireBeCancelled = reservationInfo ? canRefundCarHireItem(item, order, reservationInfo.cancellationPolicy) : false
  const carHireFreeCancellationPeriodEndDate = reservationInfo?.cancellationPolicy?.maxDate

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', {
      action: 'selectOrder',
      payload: {
        canCarHireBeCancelled,
        carHireFreeCancellationPeriodEndDate,
        id_orders: order.id,
      },
    }))
  }, [canCarHireBeCancelled, carHireFreeCancellationPeriodEndDate, dispatch, order.id])

  if (!reservation?.vehicle) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['listInclusions', 'showFinePrint', 'changeRoom']))

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineCarIcon size="XS" />}>Car hire</BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">Upcoming</StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">Past</StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              <Group direction="horizontal" verticalAlign="end" gap={4} wrap="wrap">
                <LesaPurchaseSummaryTitle orderId={order.id}>
                  {reservation?.vehicle?.model}
                </LesaPurchaseSummaryTitle>{' '}
                {!reservation?.vehicle?.guarenteedModel && <BodyText wrap="no-wrap" as="span" variant="medium" colour="neutral-three">or similar</BodyText>}
              </Group>
              <BodyTextBlock variant="small" colour="neutral-three">
                <CarHirePurchaseSummaryLocation
                  sameLocation={sameLocation}
                  pickUpLocationName={reservation?.pickUp?.locationName}
                  dropOffLocationName={reservation?.dropOff?.locationName}
                  order={order}
                />
              </BodyTextBlock>
              {order.bookingNumber && <BodyTextBlock variant="small" colour="neutral-two"><strong>Booking ID:</strong> {order.bookingNumber}</BodyTextBlock>}{' '}
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                Select
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}
